import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, Phone, User, LogOut } from "lucide-react";
import CalendlyButton from "../components/CalendlyButton";
import { NAV_SECTIONS } from "../config/navConfig";

const Header = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null); // 'why', 'explore', 'tools', 'account'
  const navigate = useNavigate();

  const isLoggedIn = !!localStorage.getItem("npcAuthToken");

  const closeMenu = () => setMenuOpen(false);
  const closeDropdown = () => setOpenDropdown(null);

  const handleLogout = () => {
    localStorage.removeItem("npcAuthToken");
    localStorage.removeItem("currentUser");
    navigate("/login");
  };

  const renderDropdown = (items, onClose) => (
    <div
      onMouseLeave={onClose}
      className="absolute left-0 mt-2 bg-white border rounded shadow-lg z-50 min-w-[180px]"
    >
      {items.map((item) => (
        <Link
          key={item.to}
          to={item.to}
          className="block px-4 py-2 hover:bg-gray-100"
          onClick={() => {
            onClose();
            closeMenu();
          }}
        >
          {item.label}
          {item.badge && (
            <span className="ml-2 bg-red-500 text-white text-[10px] px-1.5 rounded-full font-bold animate-pulse">
              {item.badge}
            </span>
          )}
        </Link>
      ))}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <div className="max-w-7xl mx-auto px-4 py-2 flex justify-between items-center h-[72px]">
        {/* Left: Logo & Mobile Toggle */}
        <div className="flex items-center space-x-3">
          <button className="md:hidden text-[#da3e13]" onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
          <Link to="/" className="flex items-center space-x-2 h-14">
            <img src="/NPC.png" alt="NPC Logo" className="h-full object-contain" />
          </Link>
        </div>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center space-x-6 text-[16px] font-medium text-[#003366]">
          {NAV_SECTIONS.top.map((item) =>
            item.dropdown ? (
              <div key={item.label} className="relative">
                <button
                  onClick={() =>
                    setOpenDropdown(openDropdown === item.group ? null : item.group)
                  }
                  className="hover:text-[#fbb40b] flex items-center"
                >
                  {item.label}
                  {item.badge && (
                    <span className="ml-1 text-red-500 font-bold animate-pulse">
                      🔥
                    </span>
                  )}
                  ▼
                </button>
                {openDropdown === item.group &&
                  renderDropdown(item.dropdown, closeDropdown)}
              </div>
            ) : (
              <Link key={item.to} to={item.to} className="hover:text-[#fbb40b] flex items-center">
                {item.label}
                {item.badge && (
                  <span className="ml-1 text-red-500 font-bold animate-pulse">
                    🔥
                  </span>
                )}
              </Link>
            )
          )}


          {/* Explore */}
          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === "explore" ? null : "explore")}
              className="hover:text-[#fbb40b] flex items-center"
            >
              Discover ▼
            </button>
            {openDropdown === "explore" && renderDropdown(NAV_SECTIONS.explore, closeDropdown)}
          </div>

          {/* Tools */}
          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === "tools" ? null : "tools")}
              className="hover:text-[#fbb40b] flex items-center"
            >
              Tools ▼
            </button>
            {openDropdown === "tools" && renderDropdown(NAV_SECTIONS.tools, closeDropdown)}
          </div>

          {/* Account */}
          <div className="relative">
            <button
              onClick={() => setOpenDropdown(openDropdown === "account" ? null : "account")}
              className="text-[#003366] font-medium hover:text-[#fbb40b] flex items-center"
            >
              <User className="mr-1" size={18} />
              Account ▼
            </button>
            {openDropdown === "account" && (
              <div
                onMouseLeave={closeDropdown}
                className="absolute right-0 mt-2 bg-white border rounded shadow-lg z-50 min-w-[160px] text-sm"
              >
                {(isLoggedIn
                  ? NAV_SECTIONS.account.loggedIn
                  : NAV_SECTIONS.account.loggedOut
                ).map((item) =>
                  item.action === "logout" ? (
                    <button
                      key={item.label}
                      onClick={handleLogout}
                      className={`w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center ${item.danger ? "text-red-600" : ""
                        }`}
                    >
                      <LogOut className="mr-2" size={16} />
                      {item.label}
                    </button>
                  ) : (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => {
                        closeDropdown();
                        closeMenu();
                      }}
                      className={`block px-4 py-2 hover:bg-gray-100 ${item.highlight ? "text-[#da3e13] font-semibold" : ""
                        }`}
                    >
                      <>
                        {item.label}
                        {item.badge && (
                          <span className="ml-1 text-red-500 font-bold animate-pulse">🔥</span>
                        )}
                      </>

                    </Link>
                  )
                )}
              </div>
            )}
          </div>
        </nav>
      </div>

      {/* Mobile Nav */}
      {menuOpen && (
        <div className="md:hidden bg-white px-4 pb-4 space-y-3 text-sm font-medium shadow-md text-[#003366]">
          {[...NAV_SECTIONS.top, ...NAV_SECTIONS.explore, ...NAV_SECTIONS.tools].map((item) =>
            item.dropdown ? (
              <div key={item.label} className="space-y-1">
                <span className="block text-gray-700 font-semibold mt-2">{item.label}</span>
                {item.dropdown.map((sub) => (
                  <Link
                    key={sub.to}
                    to={sub.to}
                    onClick={closeMenu}
                    className="block pl-4 text-[#003366] hover:text-[#fbb40b]"
                  >
                    {sub.label}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={item.to}
                to={item.to}
                onClick={closeMenu}
                className={`block w-full hover:text-[#fbb40b] ${item.highlight ? "text-[#da3e13] font-semibold" : ""
                  }`}
              >
                {item.label}
              </Link>
            )
          )}

          {(isLoggedIn ? NAV_SECTIONS.account.loggedIn : NAV_SECTIONS.account.loggedOut).map(
            (item) =>
              item.action === "logout" ? (
                <button
                  key={item.label}
                  onClick={handleLogout}
                  className={`block w-full text-left ${item.danger ? "text-red-600 hover:text-red-800" : ""
                    }`}
                >
                  {item.label}
                </button>
              ) : (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={closeMenu}
                  className={`block w-full hover:text-[#fbb40b] ${item.highlight ? "text-[#da3e13] font-semibold" : ""
                    }`}
                >
                  {item.label}
                </Link>
              )
          )}

          <a href="tel:0420682960" className="flex items-center space-x-2 text-[#003366]">
            <Phone size={18} /> <span>Call Now</span>
          </a>

          <CalendlyButton />
        </div>
      )}
    </header>
  );
};

export default Header;
